import { ComponentConfig } from '@/components/componentRegistry';
import { Theme } from '@mui/material/styles';
import { TFunction } from 'i18next';
import { documentIcons } from '@/assets/icons';

interface SignUpConfigParams {
  theme: Theme;
  t: TFunction;
  firstName: string;
  lastName: string;
  email: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onSign: () => void;
  isFormValid: boolean;
}

/**
 * Configuration for SignUp dialog page
 * Uses config-driven UI pattern with Card instead of Dialogue
 * (Dialogue props like title/content/actions expect ReactNode, not ComponentConfig)
 */
export const getSignUpConfig = ({
  theme,
  t,
  firstName,
  lastName,
  email,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onSign,
  isFormValid,
}: SignUpConfigParams): ComponentConfig => {
  return {
    component: 'Box',
    props: {
      sx: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: '100%',
        padding: theme.spacing(2),
      },
    },
    children: [
      {
        component: 'Card',
        props: {
          elevation: 3,
          sx: {
            width: '100%',
            maxWidth: '500px',
            padding: theme.spacing(4),
            borderRadius: theme.spacing(2),
            backgroundColor:
              theme.palette.background?.paper || theme.palette.M3?.white,
          },
        },
        children: [
          {
            component: 'ColumnLayout',
            props: {
              spacing: 'lg',
              alignItems: 'center',
              fullWidth: true,
            },
            children: [
              // Logo
              {
                component: 'Box',
                props: {
                  component: 'img',
                  src: documentIcons.revolutionLogo,
                  alt: 'Revolution Logo',
                  sx: {
                    height: '48px',
                    width: 'auto',
                    objectFit: 'contain',
                    marginBottom: theme.spacing(2),
                  },
                },
              },
              // Form Fields
              getSignUpFormConfig({
                theme,
                t,
                firstName,
                lastName,
                email,
                onFirstNameChange,
                onLastNameChange,
                onEmailChange,
              }),
              // Actions
              getSignUpActionsConfig({
                theme,
                t,
                onSign,
                isFormValid,
              }),
            ],
          },
        ],
      },
    ],
  };
};

/**
 * Configuration for SignUp form fields
 */
const getSignUpFormConfig = ({
  theme,
  t,
  firstName,
  lastName,
  email,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
}: Pick<
  SignUpConfigParams,
  | 'theme'
  | 't'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'onFirstNameChange'
  | 'onLastNameChange'
  | 'onEmailChange'
>): ComponentConfig => {
  return {
    component: 'ColumnLayout',
    props: {
      spacing: 'lg',
      fullWidth: true,
      sx: {
        paddingTop: theme.spacing(2),
      },
    },
    children: [
      {
        component: 'TextField',
        props: {
          label: t('signUp.firstName', 'First Name'),
          value: firstName,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            onFirstNameChange(e.target.value),
          fullWidth: true,
          required: true,
          placeholder: t(
            'signUp.firstNamePlaceholder',
            'Enter your first name'
          ),
        },
      },
      {
        component: 'TextField',
        props: {
          label: t('signUp.lastName', 'Last Name'),
          value: lastName,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            onLastNameChange(e.target.value),
          fullWidth: true,
          required: true,
          placeholder: t('signUp.lastNamePlaceholder', 'Enter your last name'),
        },
      },
      {
        component: 'TextField',
        props: {
          label: t('signUp.email', 'Email'),
          value: email,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            onEmailChange(e.target.value),
          fullWidth: true,
          required: true,
          type: 'email',
          placeholder: t('signUp.emailPlaceholder', 'Enter your email address'),
        },
      },
    ],
  };
};

/**
 * Configuration for SignUp action buttons
 */
const getSignUpActionsConfig = ({
  theme,
  t,
  onSign,
  isFormValid,
}: Pick<
  SignUpConfigParams,
  'theme' | 't' | 'onSign' | 'isFormValid'
>): ComponentConfig => {
  return {
    component: 'Box',
    props: {
      sx: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        paddingBottom: theme.spacing(2),
      },
    },
    children: [
      {
        component: 'Button',
        props: {
          variant: 'filled',
          color: 'primary',
          onClick: onSign,
          disabled: !isFormValid,
          sx: {
            minWidth: '120px',
          },
          children: t('signUp.sign', 'Register'),
        },
      },
    ],
  };
};

export default getSignUpConfig;
